import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import { Autocomplete } from '@totalsoft/rocket-ui';
import { getTranslationByCount } from 'constants/common';

const TaskFiltersComponent = ({
  statuses,
  taskTypes,
  taskPriorities,
  clientCategories,
  filterSelections,
  translatedOptions,
  translatedStates,
  responsibles,
  onFilterValueChanged
}) => {
  const { t } = useTranslation();
  const getLimitTagsText = useCallback((more, singularTag, pluralTag) => getTranslationByCount(more, singularTag, pluralTag, t), [t]);

  const handleTaskTypesChange = useCallback(
    selectedValues => {
      // Ensure we always pass an array, even if selection is cleared
      const newValues = Array.isArray(selectedValues) ? selectedValues : [];
      onFilterValueChanged('taskTypes')(newValues);
    },
    [onFilterValueChanged]
  );

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} sm={6} md={6} lg={3}>
        <Autocomplete
          fullWidth
          value={filterSelections.option}
          label={t('Dashboard.FiltersTasks')}
          id='tasks'
          valueKey='id'
          labelKey='name'
          options={translatedOptions}
          onChange={onFilterValueChanged('option')}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={6} lg={3}>
        <Autocomplete
          fullWidth
          value={filterSelections.state?.id}
          label={t('Dashboard.FiltersState')}
          id='active'
          simpleValue={true}
          options={translatedStates}
          onChange={onFilterValueChanged('state')}
          isClearable={true}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={6} lg={3}>
        <Autocomplete
          fullWidth
          value={filterSelections.status?.id}
          label={t('Dashboard.FiltersStatus')}
          id='state'
          simpleValue={true}
          options={statuses}
          onChange={onFilterValueChanged('status')}
          isClearable={true}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={6} lg={3}>
        <Autocomplete
          fullWidth
          value={filterSelections.responsible.id}
          label={t('Dashboard.FiltersResponsible')}
          id='responsible'
          simpleValue={true}
          options={responsibles}
          onChange={onFilterValueChanged('responsible')}
          disabled={filterSelections.option && filterSelections.option.id === 0}
          isClearable={true}
        />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <Autocomplete
          fullWidth
          isMultiSelection
          withCheckboxes
          limitTags={0}
          disableCloseOnSelect={false}
          renderTags={(value, _) => {
            const numTags = value.length;
            if (numTags === 0) return null;
            return getLimitTagsText(numTags, 'Selection', 'Selections');
          }}
          value={filterSelections.taskTypes || []}
          label={t('Dashboard.FiltersTaskTypes')}
          id='taskTypes'
          options={taskTypes || []}
          onChange={handleTaskTypesChange}
        />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <Autocomplete
          fullWidth
          value={filterSelections.clientCategory}
          label={t('Dashboard.FiltersClientCategory')}
          id='clientCategory'
          options={clientCategories || []}
          onChange={onFilterValueChanged('clientCategory')}
          isClearable={true}
        />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <Autocomplete
          fullWidth
          isClearable={true}
          value={filterSelections.isHighPriority || null}
          label={t('Dashboard.FiltersPriorityTasks')}
          id='isHighPriority'
          options={taskPriorities || []}
          valueKey='value'
          labelKey='label'
          onChange={onFilterValueChanged('isHighPriority')}
        />
      </Grid>
    </Grid>
  );
};

TaskFiltersComponent.propTypes = {
  filterSelections: PropTypes.object.isRequired,
  onFilterValueChanged: PropTypes.func.isRequired,
  responsibles: PropTypes.array.isRequired,
  translatedOptions: PropTypes.array.isRequired,
  translatedStates: PropTypes.array.isRequired,
  statuses: PropTypes.array.isRequired,
  taskTypes: PropTypes.array.isRequired,
  taskPriorities: PropTypes.array.isRequired,
  clientCategories: PropTypes.array.isRequired
};

export default TaskFiltersComponent;
